// Gates PostHog persistence on HubSpot's analytics consent (GDPR).
// nuxt-posthog initializes PostHog with persistence: 'memory' and opts out of
// capturing/persistence by default (see nuxt.config.js). It also writes a
// `ph-identify` cookie via useCookie, which we clear immediately. When HubSpot
// signals analytics consent, we flip persistence to localStorage+cookie and
// opt back in.
//
// Two things worth knowing here:
//
// 1. We opt in with `captureEventName: false`. posthog-js emits an `$opt_in`
//    event by default, and it was the single highest-volume event in the
//    project — 308k in 30 days, roughly one per session, minting ~58k
//    distinct_ids a WEEK that fired one $opt_in and never appeared again. We
//    never read the event, so we don't pay to send it.
//
// 2. grantConsent() is idempotent. Three listeners are registered below (the
//    `gdpr` CustomEvent plus the two legacy `_hsp`/`_hsq` queues) and HubSpot
//    fires more than one of them, so an unguarded opt-in ran several times per
//    page load.

const PH_IDENTIFY_COOKIE = 'ph-identify';

const clearPhIdentifyCookie = () => {
    if (typeof document === 'undefined') return;
    document.cookie = `${PH_IDENTIFY_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

// ticketType arrives from HubSpot as an array; PostHog breakdowns want a scalar.
const firstValue = (val) => (Array.isArray(val) ? val[0] ?? null : val ?? null);

export default defineNuxtPlugin({
    name: 'posthog-consent',
    enforce: 'post',
    setup(nuxtApp) {
        const posthog = nuxtApp.$clientPosthog;
        if (!posthog || typeof posthog.set_config !== 'function') return;

        clearPhIdentifyCookie();

        const { loggedIn, user } = useUserSession();

        let optedIn = false;
        let identifiedFor = null;

        // Person profiles are 'identified_only', so until this runs a logged-in
        // attendee has no profile and nothing can be segmented by ticket type.
        // Deliberately no email or name: HubSpot is the system of record for PII,
        // and contactId is enough to join back to it.
        const identifyAttendee = () => {
            if (!optedIn || !loggedIn.value) return;
            const attendee = user.value;
            const contactId = attendee?.contactId;
            if (!contactId || identifiedFor === contactId) return;
            identifiedFor = contactId;

            posthog.identify?.(String(contactId), {
                ticket_type: firstValue(attendee.ticketType),
                ticket_name: firstValue(attendee.ticketName),
                job_title: attendee.title ?? null,
                company: attendee.company ?? null,
            });
        };

        const grantConsent = () => {
            if (!optedIn) {
                // Another tab/page-load may already have opted this browser in.
                if (posthog.has_opted_in_capturing?.()) {
                    optedIn = true;
                } else {
                    posthog.set_config({
                        persistence: 'localStorage+cookie',
                        disable_persistence: false,
                    });
                    posthog.opt_in_capturing({ captureEventName: false });
                    optedIn = true;
                }
            }
            identifyAttendee();
        };

        const onConsent = (consent) => {
            if (consent?.allowed || consent?.categories?.analytics) grantConsent();
        };

        // Identify attendees who log in after consent was already granted, and
        // reset on logout so the next person on this browser isn't merged into
        // the previous attendee's profile.
        watch(loggedIn, (isLoggedIn, wasLoggedIn) => {
            if (isLoggedIn) {
                identifyAttendee();
            } else if (wasLoggedIn) {
                identifiedFor = null;
                posthog.reset?.();
            }
        });

        // HubSpot's banner dispatches a `gdpr` CustomEvent with consent in event.detail.
        document.addEventListener('gdpr', (event) => onConsent(event.detail));

        // Legacy queue API — kept as a fallback for older HubSpot bundles.
        window._hsp = window._hsp || [];
        window._hsp.push(['addPrivacyConsentListener', (c) => onConsent(c)]);
        if (Array.isArray(window._hsq)) {
            window._hsq.push(['addPrivacyConsentListener', (c) => onConsent(c)]);
        }
    },
});
